<template>
  <Editor
    ref="editorElement"
    v-model="editorValue"
    :placeholder="props.data.placeholder"
    :disabled="props.data.disabled"
    :editor-style="props.data?.additionalOptions?.editorStyle === 'auto'
      ? ''
      : props.data?.additionalOptions?.editorStyle
        ? props.data?.additionalOptions?.editorStyle
        : 'min-height: 80px'"
    :class="[{ disabledHtml: props.data?.additionalOptions?.disabledHtml }]"
    :formats="editorFormats"
    :pt="{
      toolbar: {
        class: [{'hidden': props.data?.additionalOptions?.customTemplates?.length === 0}]
      }
    }"
    @selection-change="() => {
      emit('returnDataBlur', props.data?.additionalOptions?.disabledHtml ? cleanText(editorValue) : editorValue)
    }"
    @value-change="() => {
      emit('returnDataInput', props.data?.additionalOptions?.disabledHtml ? cleanText(editorValue) : editorValue)
    }"
  >
    <template
      v-if="props.data?.additionalOptions?.customToolbar"
      #toolbar
    >
      <span class="ql-formats flex items-center relative">
        <div
          v-if="props.data.disabled"
          class="absolute top-0 left-0 h-full w-full bg-white/40"
        ></div>

        <div class="space-x-2">
          <Tag
            v-for="item, i in props.data?.additionalOptions?.customTemplates"
            :key="i"
            v-tooltip.bottom.isDarkMode="item.tooltip"
            class="cursor-pointer"
            severity="info"
            :value="item.label"
            @mousedown.prevent="insertTemplate(item.template)"
          ></Tag>
        </div>
      </span>
    </template>
  </Editor>
</template>

<script setup>
let Quill
if (import.meta.client) {
  Quill = await import('quill').then(m => m.default)
}

const editorElement = ref(false)

/**
* Props
*/
const props = defineProps({
  data: {
    type: [Object],
    default: null
  }
})

/**
* Emits
*/
const emit = defineEmits(['returnData', 'returnDataBlur', 'returnDataInput'])

/**
* Editor Formats
*/
const defaultFormats = ref(null)
const editorFormats = computed(() => {
  return props.data?.additionalOptions?.formats
    ? defaultFormats.value
      ? [...new Set([...props.data.additionalOptions.formats, ...defaultFormats.value])]
      : props.data.additionalOptions.formats
    : null
})

/**
* Delete HTML and replace line breaks
*/
const cleanText = (html) => {
  if (html) {
    return html
      .replaceAll('<p><br></p>', '\n')
      ?.replace(/<p>(.*?)<\/p>/g, '$1\n')
      ?.replace(/<br\s*\/?>/g, '\n')
      ?.replace(/<\/?[^>]+(>|$)/g, '')
      ?.replaceAll('&nbsp;', ' ')
      .trim()
  } else { 
    return ''
  }
}

/*
* Not Editables Templates
*/
const nonEditableTemplates = computed(() =>
  props.data?.additionalOptions?.customTemplates?.map(t => t.template) || []
)

/*
* Register Custom Blot for Not Editables Spans
*/
if (typeof CustomSpanBlot === 'undefined') {
  const Inline = Quill.import('blots/inline')

  class CustomSpanBlot extends Inline {
    static blotName = 'customSpan'
    static tagName = 'span'

    static create(value) {
      const node = super.create()

      if (value === true) {
        node.setAttribute('contenteditable', 'false')
      }

      return node
    }

    static formats(node) {
      return node
    }

    static value(node) {
      return node.textContent
    }
  }

  if (typeof defaultFormats.value !== Object) {
    defaultFormats.value = ['customSpan']
  } else { 
    defaultFormats.value.push('customSpan')
  }

  if (!Quill.imports['formats/customSpan']) {
    Quill.register(CustomSpanBlot)
  }
}

/*
* Replace Templates With Not Editable Spans
*/
const replaceTemplatesWithSpans = (text) => {
  if (!nonEditableTemplates.value.length || !text) return text

  const regex = new RegExp(`(${nonEditableTemplates.value.map(t => t?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g')

  return text?.replace(regex, (match) => {
    return `<span contenteditable="false">${match}</span>`
  })
}

/*
* Entry Value
*/
const editorValue = typeof props.data.formData[props.data.name] === 'undefined' ? ref(null) : (ref(JSON.parse(JSON.stringify(props.data?.additionalOptions?.disabledHtml && props.data.formData[props.data.name] ? props.data.formData[props.data.name]?.replaceAll('\n', '<br/>') : props.data.formData[props.data.name]))))
editorValue.value = replaceTemplatesWithSpans(editorValue.value)

/*
* Insert Template
*/
const insertTemplate = (template) => {
  const quill = editorElement.value?.quill
  if (!quill) return

  let range = quill.getSelection()
  if (!range) { 
    const editorLength = quill.getLength()
    quill.setSelection(editorLength, 0)
    range = quill.getSelection()
  }

  const spans = quill.root.querySelectorAll('span[contenteditable="false"]')

  for (const span of spans) {
    const spanIndex = quill.getIndex(Quill.find(span))

    if (range.index >= spanIndex && range.index <= spanIndex + span.innerText.length) {
      quill.setSelection(spanIndex + span.innerText.length)
      quill.insertText(spanIndex + span.innerText.length, template, { customSpan: true })

      editorValue.value = quill.root.innerHTML
      quill.emitter.emit('selection-change', quill.getContents(), quill.getContents(), 'api')

      return
    }
  }

  if (range.length > 0) {
    quill.deleteText(range.index, range.length)
  }

  quill.insertText(range.index, template, { customSpan: true })

  quill.setSelection(range.index + template.length)

  editorValue.value = quill.root.innerHTML
  quill.emitter.emit('selection-change', quill.getContents(), quill.getContents(), 'api')
}
</script>

<style scoped>
.custom-span {
  padding: 2px 4px;
  font-weight: bold;
  border-radius: 4px;
  user-select: none;
}
.disabledHtml {
  &:deep(p) {
    &:not(:last-child) {
      margin: 0 0 .5rem 0;
    }
  }
}
</style>
